import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X, Check, FolderPlus, Plus } from "lucide-react";
import { Button, Field, Input, Textarea, Select } from "@/design/components";
import { useCreateZeusProject, useEditZeusProject, useCreateMilestone } from "@/domain/useProjectCreation";
import { useVisibleProjects } from "@/domain/hooks";

type DraftMilestone = { description: string; targetDate: string; plannedBudget: string };

// ZEUS-132 — Create Project / Add Sub-Project modal, stratus_admin only (the
// server enforces this; the caller decides whether to render the trigger at
// all — see WorkspacePages.tsx / ProjectManagementView.tsx). One modal covers
// both cases: `presetParentProjectId` locks the parent (Add Sub-Project from
// a project's own Sub-Projects tab); its absence shows an optional parent
// picker (Create Project from the workspace-wide listing).
//
// Field scope is trimmed to what the backend actually accepts (see
// .claude/implementation-plan.md on this branch): "assign members" and
// "start/end date" have no server-side landing spot at all and aren't
// offered here. Region/district/budget aren't accepted by the create
// endpoint — they're collected anyway and applied via an automatic
// follow-up edit call right after creation, so the user experiences one
// form even though it's two requests underneath.

const STATUS_OPTIONS: { value: "draft" | "published"; label: string }[] = [
  { value: "draft", label: "Draft — not yet visible to project members" },
  { value: "published", label: "Published — live now" },
];

export function CreateProjectModal({
  open,
  onOpenChange,
  programmeId,
  workspaceId,
  presetParentProjectId,
  requestId,
  initialValues,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Raw id (workspace/programme) — real numeric-string in real-API mode,
   * demo id in demo mode. useCreateZeusProject converts as needed. */
  programmeId?: string;
  /** Workspace slug the parent-project picker should read from. Every
   * existing caller renders this modal from inside that one workspace, so
   * useVisibleProjects()'s default (the globally active workspace) already
   * matches — omit it there. The cross-deal Projects screen
   * (CreateProjectAction.tsx) is the one caller where the target deal isn't
   * the active workspace, so it passes this explicitly; without it, the
   * parent picker would list the wrong deal's projects. */
  workspaceId?: string;
  /** Set when opened as "+ Add Sub-Project" from a specific project's view —
   * locks the parent instead of offering a picker. Raw id, same as above. */
  presetParentProjectId?: string;
  /** ZEUS-138 — set when opened from the review queue to accept a pending
   * project-creation request: threaded through to createZeusProject so the
   * backend links + accepts the request atomically with the create. */
  requestId?: string;
  /** ZEUS-138 — pre-fills the form when reviewing a pending request. */
  initialValues?: { name?: string; description?: string; parentProjectId?: string };
  onCreated?: (projectId: string | number) => void;
}) {
  const isSubProject = presetParentProjectId != null;
  const isReviewingRequest = requestId != null;
  const parentCandidates = useVisibleProjects(workspaceId);
  const { createZeusProject, isPending: creating, error: createError } = useCreateZeusProject();
  const { editZeusProject, isPending: editing, error: editError } = useEditZeusProject();
  const { createMilestone, isPending: creatingMilestone } = useCreateMilestone();

  const [name, setName] = useState(initialValues?.name ?? "");
  const [description, setDescription] = useState(initialValues?.description ?? "");
  const [parentProjectId, setParentProjectId] = useState<string>(initialValues?.parentProjectId ?? "");
  const [status, setStatus] = useState<"draft" | "published">("draft");
  const [region, setRegion] = useState("");
  const [district, setDistrict] = useState("");
  const [budgeted, setBudgeted] = useState("");
  const [justification, setJustification] = useState("");
  const [milestones, setMilestones] = useState<DraftMilestone[]>([]);
  const [submitted, setSubmitted] = useState(false);

  function addMilestoneRow() {
    setMilestones((rows) => [...rows, { description: "", targetDate: "", plannedBudget: "" }]);
  }
  function updateMilestoneRow(index: number, patch: Partial<DraftMilestone>) {
    setMilestones((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }
  function removeMilestoneRow(index: number) {
    setMilestones((rows) => rows.filter((_, i) => i !== index));
  }

  // Re-sync when opened to review a (possibly different) request — the modal
  // instance is reused across reviews rather than remounted per row.
  useEffect(() => {
    if (!open) return;
    setName(initialValues?.name ?? "");
    setDescription(initialValues?.description ?? "");
    setParentProjectId(initialValues?.parentProjectId ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, requestId]);

  const isPending = creating || editing || creatingMilestone;
  const error = createError || editError;

  const canSubmit = name.trim().length > 0 && justification.trim().length > 0;

  function reset() {
    setName("");
    setDescription("");
    setParentProjectId("");
    setStatus("draft");
    setRegion("");
    setDistrict("");
    setBudgeted("");
    setJustification("");
    setMilestones([]);
    setSubmitted(false);
  }

  async function onSubmit() {
    if (!canSubmit) return;

    const effectiveParentId = isSubProject ? presetParentProjectId : parentProjectId || undefined;

    const result = await createZeusProject({
      name: name.trim(),
      description: description.trim() || undefined,
      justification: justification.trim(),
      status,
      programmeId,
      parentProjectId: effectiveParentId,
      requestId,
    });
    if (!result) return;

    const projectId = result.project.id;

    // Region/district/budget have no landing spot on create — apply them via
    // ZEUS-127's edit endpoint right after, if the user filled any in.
    if (region.trim() || district.trim() || budgeted.trim()) {
      await editZeusProject(projectId, {
        region: region.trim() || undefined,
        district: district.trim() || undefined,
        budgeted: budgeted.trim() ? Number(budgeted.trim()) : undefined,
        justification: justification.trim(),
      });
    }

    // Milestones have no landing spot on create either — same pattern,
    // one follow-up call per row the user filled in.
    const readyMilestones = milestones.filter((m) => m.description.trim());
    if (readyMilestones.length > 0) {
      await Promise.all(
        readyMilestones.map((m, i) =>
          createMilestone(projectId, {
            milestonenumber: `M${i + 1}`,
            description: m.description.trim(),
            targetDate: m.targetDate || undefined,
            plannedBudget: m.plannedBudget.trim() ? Number(m.plannedBudget.trim()) : undefined,
          }),
        ),
      );
    }

    setSubmitted(true);
    onCreated?.(projectId);
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/25 backdrop-blur-[2px] animate-fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[88vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-card border border-line bg-card shadow-overlay animate-fade-in">
          <div className="flex items-start justify-between gap-3 p-6 pb-3">
            <Dialog.Title className="text-base font-semibold tracking-tight text-ink">
              {submitted
                ? "Project created"
                : isReviewingRequest
                  ? "Review project request"
                  : isSubProject
                    ? "Add sub-project"
                    : "Create project"}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button aria-label="Close" className="rounded-md p-1 text-faint hover:bg-subtle hover:text-ink">
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {submitted ? (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6">
              <div className="flex items-start gap-3 rounded-md border border-ok/30 bg-okSoft px-3 py-3 text-sm text-ok">
                <Check className="mt-0.5 h-4 w-4 shrink-0" />
                <span><strong>{name}</strong> has been created.</span>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={reset}>Create another</Button>
                <Button onClick={() => onOpenChange(false)}>Done</Button>
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-4">
              <Dialog.Description className="text-sm leading-relaxed text-mute">
                {isReviewingRequest
                  ? "Pre-filled from the contractor's request. Editing and creating the project accepts the request."
                  : isSubProject
                    ? "Adds a sub-project under this project."
                    : "Creates a new top-level project, or a sub-project if you pick a parent below."}
              </Dialog.Description>

              <Field label="Name">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Fire Station #25 Jinja" />
              </Field>

              <Field label="Description (optional)">
                <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
              </Field>

              {!isSubProject && parentCandidates.length > 0 && (
                <Field label="Parent project (optional — leave blank for a top-level project)">
                  <Select value={parentProjectId} onChange={(e) => setParentProjectId(e.target.value)}>
                    <option value="">— Top-level project —</option>
                    {parentCandidates.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </Select>
                </Field>
              )}

              <Field label="Initial status">
                <Select value={status} onChange={(e) => setStatus(e.target.value as "draft" | "published")}>
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Select>
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Region (optional)">
                  <Input value={region} onChange={(e) => setRegion(e.target.value)} />
                </Field>
                <Field label="District (optional)">
                  <Input value={district} onChange={(e) => setDistrict(e.target.value)} />
                </Field>
              </div>

              <Field label="Budget, UGX (optional)">
                <Input
                  type="number"
                  value={budgeted}
                  onChange={(e) => setBudgeted(e.target.value)}
                  placeholder="e.g. 2600000000"
                />
              </Field>

              <Field label="Milestones (optional)">
                <div className="space-y-2">
                  {milestones.map((m, i) => (
                    <div key={i} className="flex items-start gap-2 rounded-md border border-line p-2">
                      <div className="flex-1 space-y-2">
                        <Input
                          value={m.description}
                          onChange={(e) => updateMilestoneRow(i, { description: e.target.value })}
                          placeholder="e.g. Foundation complete"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="date"
                            value={m.targetDate}
                            onChange={(e) => updateMilestoneRow(i, { targetDate: e.target.value })}
                          />
                          <Input
                            type="number"
                            value={m.plannedBudget}
                            onChange={(e) => updateMilestoneRow(i, { plannedBudget: e.target.value })}
                            placeholder="Budget, UGX"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        aria-label="Remove milestone"
                        className="rounded-md p-1 text-faint hover:bg-subtle hover:text-ink"
                        onClick={() => removeMilestoneRow(i)}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <Button variant="secondary" onClick={addMilestoneRow}>
                    <Plus className="h-4 w-4" /> Add milestone
                  </Button>
                </div>
              </Field>

              <Field label="Justification">
                <Textarea
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  rows={3}
                  placeholder="Why is this project necessary? What is it intended to achieve?"
                />
              </Field>

              {error && <p role="alert" className="text-sm text-bad">{error}</p>}
            </div>
          )}

          {!submitted && (
            <div className="flex items-center justify-end gap-2 border-t border-line p-6 pt-4">
              <Dialog.Close asChild>
                <Button variant="ghost">Cancel</Button>
              </Dialog.Close>
              <Button disabled={!canSubmit || isPending} onClick={onSubmit}>
                <FolderPlus className="h-4 w-4" />{" "}
                {isPending
                  ? "Creating…"
                  : isReviewingRequest
                    ? "Accept & create project"
                    : isSubProject
                      ? "Add sub-project"
                      : "Create project"}
              </Button>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
